import { zodResolver } from "@hookform/resolvers/zod";
import {
  conainsUpperCase,
  containsLowerCase,
  containsNumber,
  containsSpecialCharacters,
} from "pages/auth/signup/signupCard/SignupCard";
import { useEffect } from "react";
import { useForm, useWatch } from "react-hook-form";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { UserController } from "services/user/UserController";
import { Button, Card, Input, Link } from "stories";
import { PasswordChecker } from "stories/components/passwordSlider";
import { useFetchState } from "utils/customHooks/useFetchState";
import { useToast } from "utils/customHooks/useToast";
import { z } from "zod";
import classes from "./ResetPasswordCard.module.scss";

const schema = z
  .object({
    password: z
      .string()
      .min(8, "Please enter at least 8 characters")
      .refine(
        (str) => conainsUpperCase(str),
        "This field should contain at least 1 uppercase letter"
      )
      .refine(
        (str) => containsLowerCase(str),
        "This field should contain at least 1 lower letter"
      )
      .refine(
        (str) => containsNumber(str),
        "This field should contain at least 1 number"
      )
      .refine(
        (str) => containsSpecialCharacters(str),
        "One special character (!@#$%...) should be used in this field."
      ),
    passwordConfirm: z.string().min(1, "This field is required"),
  })
  .refine((data) => data.password === data.passwordConfirm, {
    message: "Passwords don't match",
    path: ["passwordConfirm"],
  });

const getDefaultValues = () => ({
  password: "",
  passwordConfirm: "",
});

const ResetPasswordCard = () => {
  const {
    formState: { errors },
    ...form
  } = useForm({
    defaultValues: getDefaultValues(),
    resolver: zodResolver(schema),
  });
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const user = UserController();
  const [fetchState, sendRequest] = useFetchState();
  const password = useWatch({
    control: form.control,
    name: "password",
  });
  const handleResetPassword = (values) => {
    const requestBody = {
      password: values.password,
      token,
    };
    sendRequest(user.resetPassword, requestBody);
  };

  useEffect(() => {
    if (
      !fetchState.isLoading &&
      !fetchState.isError &&
      fetchState.data === ""
    ) {
      showToast(
        "Password successfully changed! Redirecting to login...",
        "success"
      );
      setTimeout(() => {
        navigate("/sign-in");
      }, 2000);
    }
  }, [fetchState]); //eslint-disable-line

  if (!!!token) {
    return <Navigate to={"/password/request"} />;
  }

  return (
    <div className={classes["wrapper"]}>
      <Card>
        <div className={classes["content-wrapper"]}>
          <div className={classes["title"]}>Reset Password</div>
          <div className={classes["subtitle"]}>
            Please submit a new password!
          </div>
          <form onSubmit={form.handleSubmit(handleResetPassword)}>
            <Input
              type={"password"}
              label={"New Password"}
              placeholder={"Input Password"}
              {...form.register("password")}
              error={errors?.password?.message}
            />
            <PasswordChecker password={password} />
            <Input
              type={"password"}
              label={"Repeat New Password"}
              placeholder={"Input Password"}
              error={errors?.passwordConfirm?.message}
              {...form.register("passwordConfirm")}
            />
            <div className={classes["button-wrapper"]}>
              <Button
                label={"Confirm New Password"}
                loading={fetchState.isLoading}
              />
              <div className={classes["link-wrapper"]}>
                <Link to={"/sign-in"} inherit>
                  Login
                </Link>
              </div>
            </div>
          </form>
        </div>
      </Card>
    </div>
  );
};

export default ResetPasswordCard;
